import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { loginSchema, type LoginFormData } from '../schemas/login';
import { useAuthStore } from '../store/authStore';
import { mapLoginError, type ApiError } from '../lib/authErrors';
import { roleHome } from '../lib/constants';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';
import Logo from '../components/ui/Logo';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const getUser = useAuthStore.getState;
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  // Messaggio di successo passato via redirect (es. dopo reset password).
  const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const onSubmit = async (data: LoginFormData) => {
    setApiError(null);
    try {
      await login(data.email, data.password);
      navigate(roleHome(getUser().user?.role), { replace: true });
    } catch (err) {
      setApiError(mapLoginError(err));
    }
  };

  const hasError = apiError !== null;

  return (
    <div
      className="min-h-screen flex flex-col font-sans text-ink-dark"
      style={{ backgroundColor: '#F5F4F0' }}
    >
      {/* Radial gradient overlay */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(229,226,218,0.25) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(229,226,218,0.19) 0%, transparent 50%)',
        }}
      />

      <main className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <div className="w-full max-w-[420px]">

          {/* Logo card */}
          <div className="flex justify-center mb-8">
            <div className="bg-white px-7 py-5 rounded-[10px] border border-stone-mid shadow-[0_1px_2px_rgba(31,41,55,0.04)]">
              <Logo width={180} />
            </div>
          </div>

          {/* Main card */}
          <Card padding="lg" className="px-9 pt-10 pb-8">

            <div className="mb-7">
              <h1 className="text-2xl font-semibold tracking-tight mb-1.5">
                {t('auth.login.titolo')}
              </h1>
              <p className="text-[14px] text-ink-soft">
                {t('auth.login.sottotitolo')}
              </p>
            </div>

            {/* Success banner (es. dopo reset password) */}
            {successMessage && !hasError && (
              <div
                role="status"
                className="flex gap-3 px-3.5 py-3 mb-5 bg-green-50 border border-green-200 rounded-lg items-start"
              >
                <CheckCircle2 size={18} className="text-sage shrink-0 mt-px" />
                <div className="text-[13px] text-green-900 leading-snug">
                  {successMessage}
                </div>
              </div>
            )}

            {/* API error alert */}
            {hasError && (
              <div
                role="alert"
                data-testid="login-error"
                className="flex gap-3 px-3.5 py-3 mb-5 bg-red-50 border border-red-200 rounded-lg items-start"
              >
                <AlertCircle size={18} className="text-accent-danger shrink-0 mt-px" />
                <div>
                  <div className="text-[13px] font-semibold text-accent-danger mb-0.5">
                    {apiError.title}
                  </div>
                  <div className="text-[13px] text-red-900 leading-snug">
                    {apiError.message}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="mb-4">
                <Input
                  id="email"
                  type="email"
                  label={t('auth.login.label_email')}
                  autoComplete="email"
                  placeholder="nome.cognome@ellezeta.it"
                  disabled={isSubmitting}
                  error={errors.email?.message}
                  icon={
                    <Mail
                      size={18}
                      className={hasError || errors.email ? 'text-accent-danger' : 'text-ink-soft'}
                    />
                  }
                  {...register('email', {
                    onChange: () => hasError && setApiError(null),
                  })}
                />
              </div>

              <div className="mb-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('auth.login.label_password')}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  error={errors.password?.message}
                  icon={
                    <Lock
                      size={18}
                      className={hasError || errors.password ? 'text-accent-danger' : 'text-ink-soft'}
                    />
                  }
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={isSubmitting}
                      aria-label={showPassword ? t('auth.login.nascondi_password') : t('auth.login.mostra_password')}
                      className="p-2 text-ink-soft rounded cursor-pointer disabled:cursor-not-allowed"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  {...register('password', {
                    onChange: () => hasError && setApiError(null),
                  })}
                />
              </div>

              <div className="flex justify-end mb-6">
                <button
                  type="button"
                  onClick={() => navigate('/password-dimenticata')}
                  className="text-[13px] text-sage font-medium hover:underline cursor-pointer"
                >
                  {t('auth.login.password_dimenticata')}
                </button>
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                loading={isSubmitting}
                loadingText={t('auth.login.accesso_in_corso')}
                disabled={isSubmitting}
              >
                {t('auth.login.accedi')}
              </Button>
            </form>
          </Card>
        </div>
      </main>

      <footer className="relative z-10 py-6 text-center text-[12px] text-ink-soft">
        {t('auth.login.footer_prodotto')} ·{' '}
        <span className="text-ink-mid">Zara Bespoke Technology</span>
      </footer>
    </div>
  );
}
